import { Show } from "@refinedev/antd";
import { CanAccess, useCan, useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Input, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";

import { api } from "../api";
import { DATE_TIME_FORMAT, SUPPORT_STATUS } from "../labels";
import type { SupportThreadDetail } from "../types";
import { AUDIENCE_LABELS } from "./support";

/** How often an open thread checks for new messages. */
const POLL_MS = 5000;

/**
 * /support/:id: a support conversation (permission `support.view`), refreshed every few
 * seconds; answering needs `support.reply`. Users see the team's answers as "HedefUni Destek".
 */
export function SupportShowPage() {
  const { id = "" } = useParams();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const { data: canSeeStudents } = useCan({ resource: "students", action: "list" });
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const queryKey = ["office", "support", id];
  const {
    data: thread,
    error,
    isPending,
  } = useQuery({
    queryKey,
    queryFn: () => api<SupportThreadDetail>(`/api/office/support/${encodeURIComponent(id)}`),
    refetchInterval: POLL_MS,
  });

  const count = thread?.messages.length ?? 0;
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
    // Opening marks the thread read: the menu's red count goes down.
    void queryClient.invalidateQueries({ queryKey: ["office", "badges"] });
  }, [count, queryClient]);

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      await api(`/api/office/support/${encodeURIComponent(id)}/messages`, {
        method: "POST",
        body: { body },
      });
      setDraft("");
      await queryClient.invalidateQueries({ queryKey });
    } catch (sendError) {
      open?.({
        type: "error",
        message: sendError instanceof Error ? sendError.message : "Hata oluştu.",
      });
    } finally {
      setSending(false);
    }
  }

  async function setResolved(resolved: boolean) {
    try {
      await api(`/api/office/support/${encodeURIComponent(id)}/status`, {
        method: "POST",
        body: { resolved },
      });
      open?.({
        type: "success",
        message: resolved ? "Konuşma çözüldü olarak işaretlendi." : "Konuşma tekrar açıldı.",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        queryClient.invalidateQueries({ queryKey: ["office", "badges"] }),
      ]);
    } catch (statusError) {
      open?.({
        type: "error",
        message: statusError instanceof Error ? statusError.message : "Hata oluştu.",
      });
    }
  }

  if (error) return <Alert type="error" showIcon message={error.message} />;

  const person = thread?.person;
  const profileLink =
    person?.studentId && canSeeStudents?.can
      ? `/students/${person.studentId}`
      : person?.tutorId && canSeeTutors?.can
        ? `/tutors/${person.tutorId}`
        : null;

  return (
    <Show
      title={person ? `Destek · ${person.name}` : "Destek"}
      isLoading={isPending}
      canEdit={false}
      canDelete={false}
      headerButtons={
        thread ? (
          <CanAccess resource="support" action="reply">
            {thread.status === "RESOLVED" ? (
              <Button onClick={() => void setResolved(false)}>Tekrar aç</Button>
            ) : (
              <Button type="primary" onClick={() => void setResolved(true)}>
                Çözüldü olarak işaretle
              </Button>
            )}
          </CanAccess>
        ) : null
      }
    >
      {thread && person ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Space wrap>
            <Tag>{AUDIENCE_LABELS[person.audience]}</Tag>
            <Tag color={SUPPORT_STATUS[thread.status]?.color}>
              {SUPPORT_STATUS[thread.status]?.label}
            </Tag>
            <Typography.Text type="secondary">{person.email}</Typography.Text>
            {profileLink ? <Link to={profileLink}>Profilini aç</Link> : null}
          </Space>
          {thread.status === "RESOLVED" && thread.resolvedAt ? (
            <Typography.Text type="secondary">
              {thread.resolvedBy ?? "Ekip"} çözüldü olarak işaretledi ·{" "}
              {dayjs(thread.resolvedAt).format(DATE_TIME_FORMAT)}. Kullanıcı tekrar yazarsa konuşma
              yeniden açılır.
            </Typography.Text>
          ) : null}
          <Card size="small">
            <div
              ref={listRef}
              role="log"
              aria-label="Mesajlar"
              style={{
                maxHeight: "55vh",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {thread.messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    alignSelf: message.fromUser ? "flex-start" : "flex-end",
                    maxWidth: "75%",
                  }}
                >
                  <Card
                    size="small"
                    styles={{ body: { whiteSpace: "pre-wrap", wordBreak: "break-word" } }}
                    style={{ background: message.fromUser ? "#f5f5f5" : "#e6f4ff" }}
                  >
                    {message.body}
                  </Card>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {message.fromUser ? person.name : (message.staffName ?? "Ekip")} ·{" "}
                    {dayjs(message.createdAt).format(DATE_TIME_FORMAT)}
                  </Typography.Text>
                </div>
              ))}
            </div>
          </Card>
          <CanAccess resource="support" action="reply">
            <Space.Compact style={{ width: "100%" }}>
              <Input.TextArea
                aria-label="Cevabın"
                value={draft}
                maxLength={2000}
                autoSize={{ minRows: 2, maxRows: 6 }}
                placeholder="Cevabını yaz (kullanıcı 'HedefUni Destek' olarak görür)"
                onChange={(event) => setDraft(event.target.value)}
                onPressEnter={(event) => {
                  if (!event.shiftKey) {
                    event.preventDefault();
                    void send();
                  }
                }}
              />
              <Button type="primary" onClick={() => void send()} loading={sending}>
                Gönder
              </Button>
            </Space.Compact>
          </CanAccess>
        </Space>
      ) : null}
    </Show>
  );
}
